import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiHeart,
    FiShield,
    FiActivity,
    FiZap,
    FiMapPin,
    FiCpu,
    FiCheckCircle,
    FiX,
    FiArrowLeft,
    FiArrowRight,
    FiTarget,
    FiLayers,
    FiTrendingUp,
} from "react-icons/fi";
import { GiRing } from "react-icons/gi";
import { IoWatchOutline } from "react-icons/io5";
import { FaChild } from "react-icons/fa";
import SectionHeader from "../SectionHeader";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/OurStory-fa.json";
import en from "./locales/OurStory-en.json";
import ar from "./locales/OurStory-ar.json";
import tr from "./locales/OurStory-tr.json";

interface StoryCardModalData {
    title: string;
    subtitle?: string;
    description: string;
    description2?: string;
    features?: string[];
    icon: React.ReactNode;
    categoryTitle: string;
}

const OurStory: React.FC = () => {
    const { t, i18n } = useTranslation();

    // Register component translations
    useEffect(() => {
        const ns = "OurStory";
        const resources = { fa, en, ar, tr } as const;

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [activeTab, setActiveTab] = useState<string>("smartcare");
    const [selectedModal, setSelectedModal] = useState<StoryCardModalData | null>(null);

    // Determine text direction
    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";
    const textAlign = isRTL ? "text-right" : "text-left";

    const tabs = [
        { id: "smartcare", title: t("tabs.0.title", { ns: "OurStory" }), icon: <FiHeart className="w-5 h-5" /> },
        { id: "products", title: t("tabs.1.title", { ns: "OurStory" }), icon: <GiRing className="w-5 h-5" /> },
        { id: "assistant", title: t("tabs.2.title", { ns: "OurStory" }), icon: <FiCpu className="w-5 h-5" /> },
        { id: "med3", title: t("tabs.3.title", { ns: "OurStory" }), icon: <FiActivity className="w-5 h-5" /> },
        { id: "experience", title: t("tabs.4.title", { ns: "OurStory" }), icon: <FiZap className="w-5 h-5" /> },
    ];

    // Auto-switch tabs every 12 seconds when no modal is open
    useEffect(() => {
        if (selectedModal !== null) return;
        const interval = setInterval(() => {
            setActiveTab((prevTab) => {
                const currentIndex = tabs.findIndex((tab) => tab.id === prevTab);
                const nextIndex = (currentIndex + 1) % tabs.length;
                return tabs[nextIndex].id;
            });
        }, 12000);

        return () => clearInterval(interval);
    }, [tabs, selectedModal]);

    const closeModal = () => setSelectedModal(null);

    const scrollToContact = () => {
        closeModal();
        const el = document.getElementById("contact");
        if (el) {
            el.scrollIntoView({ behavior: "smooth" });
        }
    };

    const tabContent: Record<string, { title: string; content: React.ReactNode }> = {
        smartcare: {
            title: t("content.smartcare.title", { ns: "OurStory" }),
            content: (
                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-6">
                    {/* Ecosystem Card */}
                    <div
                        className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10 transition-all cursor-pointer group flex flex-col justify-between"
                        onClick={() =>
                            setSelectedModal({
                                title: t("content.smartcare.ecosystem.title", { ns: "OurStory" }),
                                subtitle: t("content.smartcare.ecosystem.subtitle", { ns: "OurStory" }),
                                description: t("content.smartcare.ecosystem.description1", { ns: "OurStory" }),
                                description2: t("content.smartcare.ecosystem.description2", { ns: "OurStory" }),
                                icon: <FiHeart className="w-6 h-6" />,
                                categoryTitle: t("content.smartcare.title", { ns: "OurStory" }),
                                features: isRTL
                                    ? [
                                          "پایش پیوسته و هوشمند علائم حیاتی",
                                          "پردازش داده‌های سلامت در ابر دلبند",
                                          "اطلاع‌رسانی سریع به پزشک و اعضای خانواده",
                                          "اتصال همزمان گجت‌ها و تجهیزات پوشیدنی",
                                      ]
                                    : [
                                          "Continuous smart vital monitoring",
                                          "Cloud-based health data processing",
                                          "Instant notifications to care team",
                                          "Multi-device wearable integration",
                                      ],
                            })
                        }
                    >
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] flex items-center justify-center text-white shadow-md shadow-blue-500/20 shrink-0">
                                <FiHeart className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className={`text-lg md:text-xl font-bold text-slate-800 group-hover:text-[#0072CE] transition-colors ${textAlign}`}>
                                    {t("content.smartcare.ecosystem.title", { ns: "OurStory" })}
                                </h3>
                                <p className={`text-slate-500 text-xs sm:text-sm ${textAlign}`}>{t("content.smartcare.ecosystem.subtitle", { ns: "OurStory" })}</p>
                            </div>
                        </div>

                        <div className="space-y-3 bg-slate-50/80 p-4 sm:p-5 rounded-xl border border-slate-100 text-slate-700 text-xs sm:text-sm leading-relaxed mb-4">
                            <p className={textAlign}>{t("content.smartcare.ecosystem.description1", { ns: "OurStory" })}</p>
                        </div>

                        <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#0072CE] group-hover:text-[#0038A8] transition-colors">
                            <span>{isRTL ? "مشاهده جزئیات کامل زیست‌بوم" : "View Full Ecosystem Details"}</span>
                            {isRTL ? <FiArrowLeft className="w-4 h-4" /> : <FiArrowRight className="w-4 h-4" />}
                        </div>
                    </div>

                    {/* Location Card */}
                    <div
                        className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10 transition-all cursor-pointer group flex flex-col justify-between"
                        onClick={() =>
                            setSelectedModal({
                                title: t("content.smartcare.location.title", { ns: "OurStory" }),
                                subtitle: t("content.smartcare.location.subtitle", { ns: "OurStory" }),
                                description: t("content.smartcare.location.description1", { ns: "OurStory" }),
                                description2: t("content.smartcare.location.description2", { ns: "OurStory" }),
                                icon: <FiMapPin className="w-6 h-6" />,
                                categoryTitle: t("content.smartcare.title", { ns: "OurStory" }),
                                features: isRTL
                                    ? [
                                          "مکان‌یابی چندگانه با دقت بالا (GPS / LBS / Wi-Fi)",
                                          "تعریف محدوده امنیتی دیجیتال (Geofence)",
                                          "سیستم هشدار خروج از محدوده امن",
                                          "ثبت و تحلیل تاریخچه ترددها",
                                      ]
                                    : [
                                          "High-precision multi-location (GPS / LBS / Wi-Fi)",
                                          "Digital geofencing security boundaries",
                                          "Instant boundary breach alert system",
                                          "Travel & location history analytics",
                                      ],
                            })
                        }
                    >
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] flex items-center justify-center text-white shadow-md shadow-blue-500/20 shrink-0">
                                <FiMapPin className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className={`text-lg md:text-xl font-bold text-slate-800 group-hover:text-[#0072CE] transition-colors ${textAlign}`}>
                                    {t("content.smartcare.location.title", { ns: "OurStory" })}
                                </h3>
                                <p className={`text-slate-500 text-xs sm:text-sm ${textAlign}`}>{t("content.smartcare.location.subtitle", { ns: "OurStory" })}</p>
                            </div>
                        </div>

                        <div className="space-y-3 bg-slate-50/80 p-4 sm:p-5 rounded-xl border border-slate-100 text-slate-700 text-xs sm:text-sm leading-relaxed mb-4">
                            <p className={textAlign}>{t("content.smartcare.location.description1", { ns: "OurStory" })}</p>
                        </div>

                        <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#0072CE] group-hover:text-[#0038A8] transition-colors">
                            <span>{isRTL ? "مشاهده جزئیات سامانه مکان‌یابی" : "View Location Tracking Details"}</span>
                            {isRTL ? <FiArrowLeft className="w-4 h-4" /> : <FiArrowRight className="w-4 h-4" />}
                        </div>
                    </div>
                </motion.div>
            ),
        },
        products: {
            title: t("content.products.title", { ns: "OurStory" }),
            content: (
                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-6">
                    {/* Health Monitoring Section */}
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-slate-200/80 shadow-xs">
                        <div className="flex items-center gap-4 mb-5">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] flex items-center justify-center text-white shadow-md shadow-blue-500/20 shrink-0">
                                <FiActivity className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className={`text-lg md:text-xl font-bold text-slate-800 ${textAlign}`}>
                                    {t("content.products.healthMonitoring.title", { ns: "OurStory" })}
                                </h3>
                                <p className={`text-slate-500 text-xs sm:text-sm ${textAlign}`}>{t("content.products.healthMonitoring.subtitle", { ns: "OurStory" })}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[0, 1].map((index) => {
                                const item = t(`content.products.healthMonitoring.items.${index}`, { ns: "OurStory", returnObjects: true }) as { title: string; description: string };
                                const icons = [GiRing, FiActivity];
                                const Icon = icons[index];

                                return (
                                    <div
                                        key={index}
                                        className="bg-slate-50/80 p-5 rounded-xl border border-slate-200/70 shadow-xs hover:border-blue-300 hover:shadow-md hover:bg-white transition-all flex flex-col justify-between group cursor-pointer"
                                        onClick={() =>
                                            setSelectedModal({
                                                title: item.title,
                                                subtitle: t("content.products.healthMonitoring.subtitle", { ns: "OurStory" }),
                                                description: item.description,
                                                icon: <Icon className="w-6 h-6" />,
                                                categoryTitle: t("content.products.title", { ns: "OurStory" }),
                                                features: isRTL
                                                    ? ["حسگرهای پردقت برای تحلیل ECG و SpO2", "ارسال هشدار افت یا افزایش علائم حیاتی", "شارژدهی طولانی و طراحی ارگونومیک", "مقاوم در برابر آب و گردوغبار"]
                                                    : ["High-accuracy ECG & SpO2 sensors", "Instant vital signs anomaly alerts", "Long battery life & ergonomic design", "Water & dust resistant hardware"],
                                            })
                                        }
                                    >
                                        <div className="flex items-start gap-3.5 mb-3">
                                            <div className="w-10 h-10 rounded-lg bg-blue-50 text-[#0072CE] border border-blue-100 flex items-center justify-center shrink-0 group-hover:bg-[#0072CE] group-hover:text-white transition-colors">
                                                <Icon className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className={`font-bold text-slate-800 text-sm mb-1 group-hover:text-[#0072CE] transition-colors ${textAlign}`}>{item.title}</h4>
                                                <p className={`text-slate-600 text-xs leading-relaxed ${textAlign}`}>{item.description}</p>
                                            </div>
                                        </div>

                                        <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-xs font-bold text-[#0072CE]">
                                            <span>{isRTL ? "جزئیات محصول" : "Product Details"}</span>
                                            {isRTL ? <FiArrowLeft className="w-3.5 h-3.5" /> : <FiArrowRight className="w-3.5 h-3.5" />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Location Tracking Section */}
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-slate-200/80 shadow-xs">
                        <div className="flex items-center gap-4 mb-5">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] flex items-center justify-center text-white shadow-md shadow-blue-500/20 shrink-0">
                                <FiMapPin className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className={`text-lg md:text-xl font-bold text-slate-800 ${textAlign}`}>
                                    {t("content.products.locationTracking.title", { ns: "OurStory" })}
                                </h3>
                                <p className={`text-slate-500 text-xs sm:text-sm ${textAlign}`}>{t("content.products.locationTracking.subtitle", { ns: "OurStory" })}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[0, 1].map((index) => {
                                const item = t(`content.products.locationTracking.items.${index}`, { ns: "OurStory", returnObjects: true }) as { title: string; description: string };
                                const icons = [IoWatchOutline, FaChild];
                                const Icon = icons[index];

                                return (
                                    <div
                                        key={index}
                                        className="bg-slate-50/80 p-5 rounded-xl border border-slate-200/70 shadow-xs hover:border-blue-300 hover:shadow-md hover:bg-white transition-all flex flex-col justify-between group cursor-pointer"
                                        onClick={() =>
                                            setSelectedModal({
                                                title: item.title,
                                                subtitle: t("content.products.locationTracking.subtitle", { ns: "OurStory" }),
                                                description: item.description,
                                                icon: <Icon className="w-6 h-6" />,
                                                categoryTitle: t("content.products.title", { ns: "OurStory" }),
                                                features: isRTL
                                                    ? ["دکمه اختصاصی SOS جهت مواقع اضطراری", "قابلیت برقراری تماس صوتی امن", "مدیریت و کنترل والدین از طریق اپلیکیشن", "موقعیت‌یابی زنده و لحظه‌ای"]
                                                    : ["Dedicated SOS button for emergencies", "Secure two-way voice calling", "Parental control via mobile app", "Real-time live location tracking"],
                                            })
                                        }
                                    >
                                        <div className="flex items-start gap-3.5 mb-3">
                                            <div className="w-10 h-10 rounded-lg bg-blue-50 text-[#0072CE] border border-blue-100 flex items-center justify-center shrink-0 group-hover:bg-[#0072CE] group-hover:text-white transition-colors">
                                                <Icon className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className={`font-bold text-slate-800 text-sm mb-1 group-hover:text-[#0072CE] transition-colors ${textAlign}`}>{item.title}</h4>
                                                <p className={`text-slate-600 text-xs leading-relaxed ${textAlign}`}>{item.description}</p>
                                            </div>
                                        </div>

                                        <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-xs font-bold text-[#0072CE]">
                                            <span>{isRTL ? "جزئیات دستگاه" : "Device Details"}</span>
                                            {isRTL ? <FiArrowLeft className="w-3.5 h-3.5" /> : <FiArrowRight className="w-3.5 h-3.5" />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </motion.div>
            ),
        },
        assistant: {
            title: t("content.assistant.title", { ns: "OurStory" }),
            content: (
                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-6">
                    {/* Health Assistant */}
                    <div
                        className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10 transition-all cursor-pointer group"
                        onClick={() =>
                            setSelectedModal({
                                title: t("content.assistant.healthAssistant.title", { ns: "OurStory" }),
                                subtitle: t("content.assistant.healthAssistant.subtitle", { ns: "OurStory" }),
                                description: isRTL
                                    ? "دستیار هوشمند سلامت دلبند بر پایه هوش مصنوعی، داده‌های حیاتی شما را تحلیل کرده و توصیه‌های دقیق پزشکی و مراقبتی ارائه می‌دهد."
                                    : "Delband's AI Health Assistant analyzes vital metrics to provide personalized medical and care recommendations.",
                                icon: <FiCpu className="w-6 h-6" />,
                                categoryTitle: t("content.assistant.title", { ns: "OurStory" }),
                                features: [0, 1, 2, 3].map((idx) => t(`content.assistant.healthAssistant.features.${idx}`, { ns: "OurStory" })),
                            })
                        }
                    >
                        <div className="flex items-center gap-4 mb-5">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] flex items-center justify-center text-white shadow-md shadow-blue-500/20 shrink-0">
                                <FiCpu className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className={`text-lg md:text-xl font-bold text-slate-800 group-hover:text-[#0072CE] transition-colors ${textAlign}`}>
                                    {t("content.assistant.healthAssistant.title", { ns: "OurStory" })}
                                </h3>
                                <p className={`text-slate-500 text-xs sm:text-sm ${textAlign}`}>{t("content.assistant.healthAssistant.subtitle", { ns: "OurStory" })}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                            {[0, 1, 2, 3].map((index) => {
                                const text = t(`content.assistant.healthAssistant.features.${index}`, { ns: "OurStory" });
                                return (
                                    <div
                                        key={index}
                                        className="flex items-center gap-3 p-3.5 bg-slate-50 rounded-xl border border-slate-200/70 text-slate-700 text-xs sm:text-sm font-medium"
                                    >
                                        <div className="w-2 h-2 rounded-full bg-[#0072CE] shrink-0" />
                                        <span className={textAlign}>{text}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#0072CE]">
                            <span>{isRTL ? "جزئیات دستیار سلامت" : "Health Assistant Details"}</span>
                            {isRTL ? <FiArrowLeft className="w-4 h-4" /> : <FiArrowRight className="w-4 h-4" />}
                        </div>
                    </div>

                    {/* Child Assistant */}
                    <div
                        className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10 transition-all cursor-pointer group"
                        onClick={() =>
                            setSelectedModal({
                                title: t("content.assistant.childAssistant.title", { ns: "OurStory" }),
                                subtitle: t("content.assistant.childAssistant.subtitle", { ns: "OurStory" }),
                                description: isRTL
                                    ? "دستیار هوشمند کودک دلبند، همراهی ایمن و آموزشی برای رشد، ایمنی و پایش الگوی رفتاری و سلامتی کودکان است."
                                    : "Delband's Child AI Assistant is a safe educational companion for child growth, safety, and behavioral monitoring.",
                                icon: <FiShield className="w-6 h-6" />,
                                categoryTitle: t("content.assistant.title", { ns: "OurStory" }),
                                features: [0, 1, 2, 3].map((idx) => t(`content.assistant.childAssistant.features.${idx}`, { ns: "OurStory" })),
                            })
                        }
                    >
                        <div className="flex items-center gap-4 mb-5">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] flex items-center justify-center text-white shadow-md shadow-blue-500/20 shrink-0">
                                <FiShield className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className={`text-lg md:text-xl font-bold text-slate-800 group-hover:text-[#0072CE] transition-colors ${textAlign}`}>
                                    {t("content.assistant.childAssistant.title", { ns: "OurStory" })}
                                </h3>
                                <p className={`text-slate-500 text-xs sm:text-sm ${textAlign}`}>{t("content.assistant.childAssistant.subtitle", { ns: "OurStory" })}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                            {[0, 1, 2, 3].map((index) => {
                                const text = t(`content.assistant.childAssistant.features.${index}`, { ns: "OurStory" });
                                return (
                                    <div
                                        key={index}
                                        className="flex items-center gap-3 p-3.5 bg-slate-50 rounded-xl border border-slate-200/70 text-slate-700 text-xs sm:text-sm font-medium"
                                    >
                                        <div className="w-2 h-2 rounded-full bg-[#0072CE] shrink-0" />
                                        <span className={textAlign}>{text}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#0072CE]">
                            <span>{isRTL ? "جزئیات دستیار کودک" : "Child Assistant Details"}</span>
                            {isRTL ? <FiArrowLeft className="w-4 h-4" /> : <FiArrowRight className="w-4 h-4" />}
                        </div>
                    </div>
                </motion.div>
            ),
        },
        med3: {
            title: t("content.med3.title", { ns: "OurStory" }),
            content: (
                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-6">
                    <div className="bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] rounded-2xl p-6 text-white text-center shadow-lg shadow-blue-500/15">
                        <h3 className="text-xl sm:text-2xl font-bold mb-2">
                            {t("content.med3.hero.title", { ns: "OurStory" })}
                        </h3>
                        <p className="text-white/90 text-xs sm:text-sm max-w-xl mx-auto leading-relaxed">
                            {t("content.med3.hero.subtitle", { ns: "OurStory" })}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[0, 1, 2].map((index) => {
                            const pillarTitle = t(`content.med3.pillars.${index}.title`, { ns: "OurStory", defaultValue: "" });
                            const pillarSubtitle = t(`content.med3.pillars.${index}.subtitle`, { ns: "OurStory", defaultValue: "" });

                            const pillarFeatures: string[] = [];
                            for (let i = 0; i < 10; i++) {
                                const feature = t(`content.med3.pillars.${index}.features.${i}`, { ns: "OurStory", defaultValue: "" });
                                if (feature && feature !== `content.med3.pillars.${index}.features.${i}`) {
                                    pillarFeatures.push(feature);
                                } else {
                                    break;
                                }
                            }

                            const icons = [FiShield, FiActivity, FiHeart];
                            const Icon = icons[index];

                            return (
                                <div
                                    key={index}
                                    className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-lg transition-all flex flex-col cursor-pointer group"
                                    onClick={() =>
                                        setSelectedModal({
                                            title: pillarTitle,
                                            subtitle: pillarSubtitle,
                                            description: isRTL
                                                ? `تحول دیجیتال در مراقبت از سلامت با تمرکز بر ${pillarTitle} و ارائه خدمات پیشگیرانه دلبند.`
                                                : `Digital transformation in healthcare focusing on ${pillarTitle} with proactive Delband services.`,
                                            icon: <Icon className="w-6 h-6" />,
                                            categoryTitle: t("content.med3.title", { ns: "OurStory" }),
                                            features: pillarFeatures,
                                        })
                                    }
                                >
                                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#0072CE] border border-blue-100 flex items-center justify-center mb-3 mx-auto group-hover:bg-[#0072CE] group-hover:text-white transition-colors">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h4 className="text-base font-bold text-slate-800 text-center group-hover:text-[#0072CE] transition-colors">{pillarTitle}</h4>
                                    <p className="text-xs text-slate-500 text-center mb-4 font-medium">{pillarSubtitle}</p>

                                    <div className="space-y-2.5 mt-auto pt-3 border-t border-slate-100 mb-3">
                                        {pillarFeatures.slice(0, 3).map((item: string, featureIndex: number) => (
                                            <div key={featureIndex} className="flex items-start gap-2 text-xs text-slate-600">
                                                <FiCheckCircle className="w-3.5 h-3.5 text-[#0072CE] shrink-0 mt-0.5" />
                                                <span className={textAlign}>{item}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="pt-2 border-t border-slate-100 flex items-center justify-center text-xs font-bold text-[#0072CE]">
                                        <span>{isRTL ? "جزئیات رکن" : "View Pillar"}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>
            ),
        },
        experience: {
            title: t("content.experience.title", { ns: "OurStory" }),
            content: (
                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-6">
                    <div className="bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] rounded-2xl p-6 text-white text-center shadow-lg shadow-blue-500/15">
                        <h3 className="text-xl sm:text-2xl font-bold mb-2">
                            {t("content.experience.gamification.title", { ns: "OurStory" })}
                        </h3>
                        <p className="text-white/90 text-xs sm:text-sm max-w-xl mx-auto leading-relaxed">
                            {t("content.experience.gamification.subtitle", { ns: "OurStory" })}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div
                            className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
                            onClick={() =>
                                setSelectedModal({
                                    title: t("content.experience.achievements.title", { ns: "OurStory" }),
                                    subtitle: t("content.experience.gamification.subtitle", { ns: "OurStory" }),
                                    description: isRTL
                                        ? "سیستم انگیزشی دلبند با ایجاد اهداف سلامت روزانه، کاربران را به حفظ سلامت و بهبود عادات زندگی تشویق می‌کند."
                                        : "Delband gamification engine motivates users with daily health milestones and progress metrics.",
                                    icon: <FiZap className="w-6 h-6" />,
                                    categoryTitle: t("content.experience.title", { ns: "OurStory" }),
                                    features: [0, 1, 2].map((idx) => {
                                        const item = t(`content.experience.achievements.items.${idx}`, { ns: "OurStory", returnObjects: true }) as { text: string; progress: number };
                                        return `${item.text} - ${item.progress}% ${isRTL ? "پیشرفت" : "Progress"}`;
                                    }),
                                })
                            }
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#0072CE] border border-blue-100 flex items-center justify-center group-hover:bg-[#0072CE] group-hover:text-white transition-colors">
                                    <FiZap className="w-5 h-5" />
                                </div>
                                <h4 className={`text-base font-bold text-slate-800 group-hover:text-[#0072CE] transition-colors ${textAlign}`}>
                                    {t("content.experience.achievements.title", { ns: "OurStory" })}
                                </h4>
                            </div>

                            <div className="space-y-4 mb-4">
                                {[0, 1, 2].map((index) => {
                                    const item = t(`content.experience.achievements.items.${index}`, { ns: "OurStory", returnObjects: true }) as { text: string; progress: number };

                                    return (
                                        <div key={index} className="space-y-1.5">
                                            <div className="flex justify-between items-center text-xs text-[#0072CE] font-semibold">
                                                <span className={`text-slate-700 ${textAlign}`}>{item.text}</span>
                                                <span>{item.progress}%</span>
                                            </div>
                                            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                                                <div
                                                    style={{ width: `${item.progress}%` }}
                                                    className="h-full bg-gradient-to-r from-[#0038A8] to-[#0072CE] rounded-full"
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#0072CE]">
                                <span>{isRTL ? "جزئیات دستاوردها" : "Achievement Details"}</span>
                                {isRTL ? <FiArrowLeft className="w-3.5 h-3.5" /> : <FiArrowRight className="w-3.5 h-3.5" />}
                            </div>
                        </div>

                        <div
                            className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group"
                            onClick={() =>
                                setSelectedModal({
                                    title: t("content.experience.habits.title", { ns: "OurStory" }),
                                    subtitle: t("content.experience.gamification.subtitle", { ns: "OurStory" }),
                                    description: isRTL
                                        ? "پایش منظم عادات سلامتی مانند تحرک، مصرف آب و خواب منظم با ثبت و تحلیل مداوم زنجیره موفقیت."
                                        : "Regular tracking of healthy habits such as activity, hydration, and sleep consistency.",
                                    icon: <FiTarget className="w-6 h-6" />,
                                    categoryTitle: t("content.experience.title", { ns: "OurStory" }),
                                    features: [0, 1, 2].map((idx) => {
                                        const item = t(`content.experience.habits.items.${idx}`, { ns: "OurStory", returnObjects: true }) as { text: string; streak: number; unit: string };
                                        return `${item.text}: ${item.streak} ${item.unit}`;
                                    }),
                                })
                            }
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#0072CE] border border-blue-100 flex items-center justify-center group-hover:bg-[#0072CE] group-hover:text-white transition-colors">
                                    <FiTarget className="w-5 h-5" />
                                </div>
                                <h4 className={`text-base font-bold text-slate-800 group-hover:text-[#0072CE] transition-colors ${textAlign}`}>
                                    {t("content.experience.habits.title", { ns: "OurStory" })}
                                </h4>
                            </div>

                            <div className="space-y-3 mb-4">
                                {[0, 1, 2].map((index) => {
                                    const item = t(`content.experience.habits.items.${index}`, { ns: "OurStory", returnObjects: true }) as { text: string; streak: number; unit: string };

                                    return (
                                        <div key={index} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs">
                                            <span className={`text-slate-700 font-medium ${textAlign}`}>{item.text}</span>
                                            <span className="bg-blue-100 text-[#0072CE] font-bold px-2.5 py-1 rounded-full text-[11px]">
                                                {item.streak} {item.unit}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#0072CE]">
                                <span>{isRTL ? "جزئیات زنجیره عادات" : "Habit Tracking Details"}</span>
                                {isRTL ? <FiArrowLeft className="w-3.5 h-3.5" /> : <FiArrowRight className="w-3.5 h-3.5" />}
                            </div>
                        </div>
                    </div>
                </motion.div>
            ),
        },
    };

    return (
        <section dir={dir} id="our-story" className="pb-16 pt-32 relative overflow-hidden">
            <SectionHeader
                regularText={t("sectionHeader.regularText", { ns: "OurStory" })}
                highlightedText={t("sectionHeader.highlightedText", { ns: "OurStory" })}
                descriptionText={t("sectionHeader.descriptionText", { ns: "OurStory" })}
                className="pb-8"
            />
            <div className="container mx-auto relative pb-14 md:pb-24">
                <div className={`flex flex-col ${isRTL ? "lg:flex-row-reverse" : "lg:flex-row"} gap-8 max-w-6xl mx-auto`}>
                    <div className="lg:w-3/4 order-2 lg:order-1">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="bg-white/95 backdrop-blur-xl rounded-3xl p-6 md:p-8 shadow-xl shadow-slate-200/50 border border-slate-200/80"
                        >
                            <AnimatePresence mode="wait">
                                <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
                                    <h2 className={`text-xl md:text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3 border-b border-slate-100 pb-4 ${textAlign}`}>
                                        {tabContent[activeTab as keyof typeof tabContent].title}
                                    </h2>
                                    {tabContent[activeTab as keyof typeof tabContent].content}
                                </motion.div>
                            </AnimatePresence>
                        </motion.div>
                    </div>

                    <div className="lg:w-1/4 order-1 lg:order-2">
                        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-4 sm:p-5 shadow-lg shadow-slate-200/40 border border-slate-200/80 sticky top-24">
                            <h3 className={`text-base font-bold text-slate-800 mb-3 sm:mb-4 ${isRTL ? "pr-1" : "pl-1"} ${textAlign}`}>{t("sideMenu.title", { ns: "OurStory" })}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-1.5 sm:gap-2 lg:gap-0 lg:space-y-1.5">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        type="button"
                                        className={`flex items-center w-full px-3.5 py-2.5 rounded-xl transition-all duration-300 cursor-pointer ${
                                            activeTab === tab.id
                                                ? "bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] text-white shadow-md shadow-blue-500/20 font-medium"
                                                : "hover:bg-blue-50/60 text-slate-600 hover:text-[#0072CE] bg-slate-50/70 lg:bg-transparent"
                                        } text-start`}
                                    >
                                        <span className={`${isRTL ? "ml-2.5" : "mr-2.5"} shrink-0`}>{tab.icon}</span>
                                        <span className="text-sm font-medium">{tab.title}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Redesigned Structured Card Modal */}
            <AnimatePresence>
                {selectedModal !== null && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto"
                        onClick={closeModal}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 15 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 15 }}
                            transition={{ type: "spring", duration: 0.4 }}
                            className="relative max-w-2xl w-full max-h-[88vh] bg-white backdrop-blur-2xl p-0 rounded-3xl shadow-2xl border border-slate-100 overflow-y-auto my-auto scrollbar-thin scrollbar-thumb-slate-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Top Gradient Line Accent */}
                            <div className="h-1.5 bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] w-full sticky top-0 z-30"></div>

                            {/* Close Button */}
                            <button
                                onClick={closeModal}
                                className="absolute top-4 end-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center text-sm transition-colors z-30"
                                aria-label="Close"
                            >
                                <FiX className="w-4 h-4" />
                            </button>

                            {/* Header Section */}
                            <div className="p-6 sm:p-8 pb-4 border-b border-slate-100">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-12 h-12 rounded-2xl bg-blue-100 text-[#0072CE] flex items-center justify-center shrink-0">
                                        {selectedModal.icon}
                                    </div>
                                    <div>
                                        <span className="text-xs font-bold text-[#0072CE] bg-blue-50 px-2.5 py-0.5 rounded-full border border-blue-100">
                                            {selectedModal.categoryTitle}
                                        </span>
                                        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">
                                            {selectedModal.title}
                                        </h3>
                                    </div>
                                </div>
                                {selectedModal.subtitle && (
                                    <p className="text-slate-500 text-xs sm:text-sm font-medium mt-1">
                                        {selectedModal.subtitle}
                                    </p>
                                )}
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 sm:p-8 space-y-6">
                                {/* Description Card */}
                                <div className="bg-gradient-to-br from-blue-50/70 to-slate-50 p-4 sm:p-5 rounded-2xl border border-blue-100/80 space-y-3">
                                    <div className="flex items-center gap-2 text-[#0072CE] font-bold text-xs uppercase tracking-wider">
                                        <FiTrendingUp className="w-4 h-4" />
                                        <span>{isRTL ? "نگاه کلی و معرفی راهکار" : "Solution Overview"}</span>
                                    </div>
                                    <p className="text-slate-700 text-xs sm:text-sm leading-relaxed">
                                        {selectedModal.description}
                                    </p>
                                    {selectedModal.description2 && (
                                        <p className="text-slate-600 text-xs sm:text-sm leading-relaxed pt-2 border-t border-blue-100/60">
                                            {selectedModal.description2}
                                        </p>
                                    )}
                                </div>

                                {/* Features List */}
                                {selectedModal.features && selectedModal.features.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                                            <FiCheckCircle className="w-4 h-4 text-[#0072CE]" />
                                            <span>{isRTL ? "قابلیت‌ها و ویژگی‌های کلیدی" : "Key Capabilities"}</span>
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {selectedModal.features.map((feat, fIdx) => (
                                                <div
                                                    key={fIdx}
                                                    className="flex items-start gap-2.5 p-3.5 rounded-xl bg-slate-50 border border-slate-100 text-xs sm:text-sm text-slate-700 font-medium"
                                                >
                                                    <FiCheckCircle className="w-4 h-4 text-[#0072CE] shrink-0 mt-0.5" />
                                                    <span>{feat}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-slate-100">
                                    <motion.button
                                        className="flex-1 bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] text-white py-3.5 px-6 rounded-xl font-semibold text-sm shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 transition-all text-center flex items-center justify-center gap-2"
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        onClick={scrollToContact}
                                    >
                                        <span>{isRTL ? "درخواست مشاوره و سفارش" : "Request Consultation & Order"}</span>
                                        {isRTL ? <FiArrowLeft className="w-4 h-4" /> : <FiArrowRight className="w-4 h-4" />}
                                    </motion.button>
                                    <button
                                        className="px-5 py-3.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold transition-colors"
                                        onClick={closeModal}
                                    >
                                        {isRTL ? "بستن" : "Close"}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default OurStory;
